import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { propertyService } from '../../../services/api';
import { PageContainer, PageHeader } from '../../../components/Layout';
import {
  Card,
  CardContent,
  StatusBadge,
  Button,
  Input,
  Select,
  Pagination,
  EmptyState,
  Spinner,
  Skeleton,
  SkeletonGrid,
} from '../../../components/UI';
import {
  Building2,
  Plus,
  Search,
  MapPin,
  DollarSign,
  Key,
  Copy,
  RefreshCw,
  Home,
  Warehouse,
  Building,
} from 'lucide-react';

const TYPE_OPTIONS = [
  { value: '', label: 'Todos los tipos' },
  { value: 'apartment', label: 'Apartamento' },
  { value: 'house', label: 'Casa' },
  { value: 'warehouse', label: 'Bodega' },
  { value: 'commercial', label: 'Comercial' },
  { value: 'office', label: 'Oficina' },
  { value: 'land', label: 'Terreno' }
];

const STATUS_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  { value: 'available', label: 'Disponible' },
  { value: 'rented', label: 'Rentada' },
  { value: 'maintenance', label: 'Mantenimiento' },
  { value: 'unavailable', label: 'No disponible' }
];

/**
 * PropertyCard Component - Enhanced property card with hover effects
 */
const PropertyCard = ({ property, onGenerateCode, onCopyCode, generatingCode }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'apartment':
        return Building;
      case 'house':
        return Home;
      case 'warehouse':
        return Warehouse;
      default:
        return Building2;
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      apartment: 'Apartamento',
      house: 'Casa',
      warehouse: 'Bodega'
    };
    return labels[type] || type;
  };

  const TypeIcon = getTypeIcon(property.type);

  return (
    <Link to={`/rentalbiz/properties/${property.id}`}>
      <Card 
        variant="hover" 
        className="h-full overflow-hidden group"
      >
        {/* Image Placeholder */}
        <div className="relative aspect-video bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
          <div className="absolute inset-0 bg-primary-600/0 group-hover:bg-primary-600/5 transition-colors" />
          <div className="p-4 bg-white/80 rounded-xl shadow-soft">
            <TypeIcon className="w-10 h-10 text-gray-400 group-hover:text-primary-500 transition-colors" />
          </div>
          
          {/* Status Badge Overlay */}
          <div className="absolute top-3 right-3">
            <StatusBadge status={property.status} size="sm" />
          </div>
        </div>

        <CardContent className="p-4">
          {/* Property Info */}
          <div className="space-y-2">
            <h3 className="font-semibold text-gray-900 text-lg line-clamp-1 group-hover:text-primary-600 transition-colors">
              {property.name}
            </h3>
            <div className="flex items-start text-sm text-gray-500">
              <MapPin className="w-4 h-4 mr-1.5 flex-shrink-0 mt-0.5" />
              <span className="line-clamp-1">{property.address}, {property.city}</span>
            </div>
          </div>

          {/* Invitation Code Section */}
          <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500 mb-1 flex items-center">
                  <Key className="w-3 h-3 mr-1" />
                  Código de invitación
                </p>
                {property.invitation_code ? (
                  <div className="flex items-center space-x-2">
                    <code className="text-sm font-mono bg-white px-2 py-1 rounded border border-gray-200 text-primary-600 truncate">
                      {property.invitation_code}
                    </code>
                    <button
                      onClick={(e) => onCopyCode(property.invitation_code, e)}
                      className="p-1 text-gray-400 hover:text-primary-600 transition-colors"
                      title="Copiar código"
                    >
                      <Copy className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">No generado</p>
                )}
              </div>
              <button
                onClick={(e) => onGenerateCode(property.id, e)}
                disabled={generatingCode}
                className="ml-2 flex items-center text-xs bg-primary-100 text-primary-700 px-2.5 py-1.5 rounded-lg hover:bg-primary-200 disabled:opacity-50 transition-colors"
              >
                {generatingCode ? (
                  <Spinner size="sm" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5 mr-1" />
                )}
                {property.invitation_code ? 'Regenerar' : 'Generar'}
              </button>
            </div>
          </div>

          {/* Price and Type */}
          <div className="mt-4 flex items-center justify-between pt-3 border-t border-gray-100">
            <div className="flex items-baseline">
              <DollarSign className="w-4 h-4 text-primary-600 mr-1" />
              <span className="text-xl font-bold text-primary-600">
                {formatCurrency(property.monthly_rent)}
              </span>
              <span className="text-sm text-gray-500 ml-1">/mes</span>
            </div>
            <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
              {getTypeLabel(property.type)}
            </span>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

/**
 * PropertiesList Component - Properties listing page
 */
const PropertiesList = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    status: '',
    city: ''
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 12, total: 0, pages: 0 });
  const [generatingCode, setGeneratingCode] = useState({});

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchProperties();
    }, 300);

    return () => clearTimeout(timer);
  }, [pagination.page, pagination.limit, filters.search, filters.type, filters.status, filters.city]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      const response = await propertyService.getAll({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      });
      setProperties(response.data.data.properties);
      setPagination(prev => ({
        ...prev,
        ...response.data.data.pagination
      }));
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const generateInvitationCode = async (propertyId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      setGeneratingCode(prev => ({ ...prev, [propertyId]: true }));
      const response = await propertyService.generateInvitationCode(propertyId);
      
      // Update the property with the new code
      setProperties(prev => prev.map(prop => 
        prop.id === propertyId 
          ? { ...prop, invitation_code: response.data.data.invitation_code }
          : prop
      ));
      
      // Copy code to clipboard
      await navigator.clipboard.writeText(response.data.data.invitation_code);
      
      // Show success notification (you could use a toast here)
      alert(`Código generado: ${response.data.data.invitation_code}\n¡Copiado al portapapeles!`);
    } catch (error) {
      console.error('Error generating invitation code:', error);
      alert('Error al generar el código de invitación');
    } finally {
      setGeneratingCode(prev => ({ ...prev, [propertyId]: false }));
    }
  };

  const copyToClipboard = async (code, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    try {
      await navigator.clipboard.writeText(code);
      alert('¡Código copiado al portapapeles!');
    } catch (error) {
      console.error('Error copying to clipboard:', error);
    }
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, page }));
  };

  return (
    <PageContainer>
      <div className="space-y-6">
        {/* Page Header */}
        <PageHeader
          title="Propiedades"
          subtitle="Gestiona tus propiedades"
          actions={
            <Link to="/rentalbiz/properties/new">
              <Button variant="primary" className="flex items-center">
                <Plus className="w-5 h-5 mr-2" />
                Nueva Propiedad
              </Button>
            </Link>
          }
        />

        {/* Filters Card */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Search */}
              <div className="lg:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Buscar por nombre o dirección..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Type Filter */}
              <Select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                placeholder="Tipo de propiedad"
                options={TYPE_OPTIONS}
              />

              {/* Status Filter */}
              <Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                placeholder="Estado"
                options={STATUS_OPTIONS}
              />

              {/* City Filter */}
              <Input
                placeholder="Ciudad..."
                value={filters.city}
                onChange={(e) => handleFilterChange('city', e.target.value)}
              />
            </div>
          </CardContent>
        </Card>

        {/* Properties Grid */}
        {loading ? (
          <SkeletonGrid 
            count={6} 
            columns={3}
            className="grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
          />
        ) : properties.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <EmptyState
                icon={Building2}
                title="No hay propiedades"
                description="Agrega tu primera propiedad para comenzar a gestionar tus rentas"
                action={
                  <Link to="/rentalbiz/properties/new">
                    <Button variant="primary">
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar Propiedad
                    </Button>
                  </Link>
                }
              />
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => (
              <PropertyCard
                key={property.id}
                property={property}
                onGenerateCode={generateInvitationCode}
                onCopyCode={copyToClipboard}
                generatingCode={generatingCode[property.id]}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.pages > 1 && (
          <Pagination
            current={pagination.page}
            total={pagination.pages}
            onChange={handlePageChange}
            showInfo
            totalItems={pagination.total}
          />
        )}
      </div>
    </PageContainer>
  );
};

export default PropertiesList;
